import type { BECategory } from 'constants/categories';
import type { MultivariateEventType } from 'constants/multivariateEventCollection';
import type { ColorsPalette } from 'models/colorsPalette';

import type { EventVo } from './event';
import type { MarketResult } from './market';
import type { MilestoneVo, StructuredTargetVo } from './milestones';
import type { FeeType, Frequency } from './series';

export type MobileSearchScope = 'markets' | 'perps';

export enum PriceChangePeriodSort {
    HOUR = 'H',
    DAY = 'D',
    WEEK = 'W',
}

export type SortCriteria =
    | 'largestRecentVolume'
    | 'latestCloseDate'
    | 'earliestCloseDate'
    | 'portfolioEvents'
    | 'portfolioLtp'
    | 'portfolioContracts'
    | 'portfolioUnrealized'
    | 'portfolioRealized'
    | 'portfolioAvg'
    | 'portfolioExposure'
    | 'portfolioClosing'
    | 'portfolioValue'
    | 'portfolioVolume'
    | 'portfolioInterest'
    | 'portfolioPayoutIfWin'
    | 'portfolioFees'
    | 'portfolioAllocation'
    | 'portfolioBuy'
    // | 'portfolioSell'
    | 'portfolioTotalPercentageReturn'
    | 'portfolioTotalReturn'
    | 'portfolioMarketValue'
    | 'portfolioNewestFirst'
    | 'portfolioPriceChange'
    | 'portfolioPositionValueChange'
    | 'portfolioRecentTrades'
    | 'allRestingCost'
    | 'restingOrdersPlaced'
    | 'timeline';

export const sortsToNames: Record<SortCriteria, string> = {
    largestRecentVolume: 'Largest recent volume',
    latestCloseDate: 'Latest close date',
    earliestCloseDate: 'Earliest close date',
    portfolioEvents: 'Alphabetically',
    portfolioLtp: 'Last traded',
    portfolioContracts: 'Contracts',
    portfolioUnrealized: 'Unrealized return',
    portfolioRealized: 'Realized return',
    portfolioAvg: 'Avg price',
    portfolioExposure: 'Cost',
    portfolioClosing: 'Closing soon',
    portfolioValue: 'Value now',
    portfolioVolume: 'Volume',
    portfolioInterest: 'Open interest',
    portfolioPayoutIfWin: 'Payout if right',
    portfolioFees: 'Fees',
    portfolioAllocation: 'Allocation',
    portfolioBuy: 'Buy / Sell',
    // portfolioBuy: 'Buy price',
    // portfolioSell: 'Sell price',
    portfolioTotalPercentageReturn: 'Total percentage return',
    portfolioTotalReturn: 'Total return',
    portfolioMarketValue: 'Market value',
    portfolioNewestFirst: 'Newest first',
    allRestingCost: 'All resting cost',
    restingOrdersPlaced: 'Placed',
    portfolioPriceChange: 'Price change',
    portfolioPositionValueChange: 'Position value change',
    portfolioRecentTrades: 'Recent trades',
    timeline: 'Timeline',
};
export const sortsToShortNames: Record<SortCriteria, string> = {
    largestRecentVolume: 'Largest recent volume',
    latestCloseDate: 'Latest close date',
    earliestCloseDate: 'Earliest close date',
    portfolioEvents: 'Alphabetically',
    portfolioLtp: 'Last traded',
    portfolioContracts: 'Contracts',
    portfolioUnrealized: 'Unrealized return',
    portfolioRealized: 'Realized return',
    portfolioAvg: 'Avg price',
    portfolioExposure: 'Cost',
    portfolioClosing: 'Closing soon',
    portfolioValue: 'Value now',
    portfolioVolume: 'Volume',
    portfolioInterest: 'Open int',
    portfolioPayoutIfWin: 'Payout if right',
    portfolioFees: 'Fees',
    portfolioAllocation: 'Allocation',
    portfolioBuy: 'Buy / Sell',
    // portfolioBuy: 'Buy',
    // portfolioSell: 'Sell',
    portfolioTotalPercentageReturn: 'Total percentage return',
    portfolioTotalReturn: 'Total return',
    portfolioMarketValue: 'Market value',
    portfolioNewestFirst: 'Newest first',
    allRestingCost: 'All resting cost',
    restingOrdersPlaced: 'Placed',
    portfolioPriceChange: 'Price change',
    portfolioPositionValueChange: 'Position value change',
    portfolioRecentTrades: 'Recent trades',
    timeline: 'Timeline',
};

export type FilterOption =
    | 'All'
    | 'New'
    | 'Closing soon'
    | 'High volume'
    | 'Open'
    | 'Closed'
    | 'health'
    | 'economics'
    | 'climate'
    | 'politics'
    | 'financials'
    | 'world'
    | 'culture'
    | 'science'
    | 'general'
    | 'Determined'
    | 'Profit'
    | 'Loss'
    | 'Deposit'
    | 'Withdrawal'
    | 'Order'
    | 'Trade'
    | 'Settlement'
    | 'Credit';

export const activityTimeFilterOptions = [
    'All time',
    'Today',
    'This week',
    'This month',
    'This year',
    'Last year',
    'Custom dates',
] as const;

export type ActivityTimeFilterOption = (typeof activityTimeFilterOptions)[number];

export enum SearchSeriesSortCriteria {
    VOLUME = 'event-volume', // Cards display event volume, so use event_volume over volume (series_volume)
    NEWEST = 'newest',
    NEWEST_EVENT = 'newest-event',
    TRENDING = 'trending',
    PRICE_DELTA = 'price-delta',
    CLOSING = 'closing',
    PRICE_BALANCE = 'price-balance',
    QUERY_MATCH = 'querymatch',
    ROUNDROBIN = 'roundrobin',
    START_TIME = 'start-time',
}

export enum SearchSeriesStatusCriteria {
    OPEN = 'open',
    CLOSED = 'closed',
    SETTLED = 'settled',
    UNOPENED = 'unopened',
}

export type SearchSeriesPageSize = 3 | 4 | 5 | 6 | 8 | 12 | 20 | 24 | 25 | 30 | 50 | 70 | 80 | 100;

/**
 * Default page size for explore category grids.
 * "Show more" button visibility depends on hasNextPage from React Query,
 * which is true when the API returns a non-empty next_cursor.
 */
export const DEFAULT_SEARCH_PAGE_SIZE: SearchSeriesPageSize = 6;

export interface SearchSeriesParams {
    query?: string;
    status?: SearchSeriesStatusCriteria[];
    frequency?: Frequency[];
    category?: BECategory[];
    tag?: string;
    reverse?: boolean;
    cursor?: string;
    page_size?: SearchSeriesPageSize;
    order_by?: SearchSeriesSortCriteria;
    excluded_categories?: BECategory[];
    excluded_tags?: string[];
    closed_lookback_days?: number;
    fuzzy_threshold?: number;
    proximity_ticker?: string;
    with_milestones?: boolean;
    scope?: string;
    competition?: string;
    competition_scope?: string;
    keywords?: string;
    hydrate?: string[];
    fee_types?: FeeType[];
    milestone_id?: string;
    /**
     * Filter the search to series whose markets carry one of these
     * structured-target ids. Single string for "filter by one target",
     * array for "filter by any of these targets" (the backend serializes
     * arrays as comma-separated values).
     */
    structured_target_ids?: string | string[];
    include_sports_derivatives?: boolean;
    /** Restrict results to player-prop / sports-derivative series only. */
    prop_only?: boolean;
    derivative_type?: string;
    /**
     * Game-grouped search over a competition's open games, props, futures, and
     * specials: each game is a top-level result with related props nested under
     * `prop_events`, and pagination counts the top-level groups. Scope with
     * `competition`.
     */
    comboable_search?: boolean;
    /** With `comboable_search`, exclude markets that cannot be used in combos. */
    comboable_only?: boolean;
    /**
     * Return the structured-target ids (teams, players) the query matched as
     * `matched_structured_target_ids` on the response.
     */
    match_structured_targets?: boolean;

    /** Timestamp (usually bucketed) used for cache busting */
    ts?: number;
}

export interface SearchRelatedSeriesParams {
    ticker?: string;
    use_event_recommendations?: boolean;
    limit?: number;
}

export const defaultNavbarSearchParams: SearchSeriesParams = {
    query: '',
    order_by: SearchSeriesSortCriteria.TRENDING,
};

export interface SearchResultMarket {
    colors?: ColorsPalette;
    ticker: string;
    yes_subtitle: string;
    yes_bid: number;
    yes_ask: number;
    last_price: number;
    price_delta: number;
    close_ts: string;
    open_ts: string;
    rulebook_variables: { [variable: string]: string };
    result?: MarketResult;
    /**
     * Legacy /v1 count: both sides of every trade, so twice the contracts traded (`/v1/events` sends the same
     * integer as `volume`, beside the one-sided `volume_contracts_fp`). Display it through
     * `searchMarketVolumeContracts`. Only a pure ranking key, such as the commodities tile order in
     * `queries/commodityScopeStats.ts`, may read it raw.
     */
    volume?: number;
    // Stubbing properties
    no_subtitle?: string;
    // BFF-style fields
    yes_bid_dollars?: string;
    yes_ask_dollars?: string;
    last_price_dollars?: string;
    expected_expiration_ts?: string;
    custom_strike?: Record<string, string>;
    score?: number;
    market_id?: string;
    title?: string;
    icon_url_dark_mode?: string;
    icon_url_light_mode?: string;
    image_url_dark_mode?: string;
    image_url_light_mode?: string;
    background_color_light_mode?: string;
    background_color_dark_mode?: string;
    image_scale?: number;
    structured_target_id?: string;
    previous_price?: number;
    previous_price_dollars?: string;
}

/**
 * Contracts traded on a search market, one side of each trade: what `/trade-api/v2`'s `volume_fp` and /v1's
 * `volume_contracts_fp` report. The search payload carries only the two-sided integer, so this halves it. Halving
 * an integer is exact in floating point; the result can end in .5 because the wire value is already truncated.
 */
export const searchMarketVolumeContracts = (market: Pick<SearchResultMarket, 'volume'>): number =>
    (market.volume ?? 0) / 2;

/**
 * A prop event nested under a game group in a `comboable_search` response.
 * When the query matched a player, `markets` is narrowed to that player.
 */
export interface SearchPropEvent {
    event_ticker: string;
    event_title: string;
    event_subtitle: string;
    /** Section label, e.g. "Touchdowns" or "Receiving". */
    scope: string;
    /** Prop classification, e.g. "player_prop". */
    sports_market_type: string;
    milestone_id?: string;
    markets: SearchResultMarket[];
}

/**
 * The sports-relevant subset of `product_metadata` (typed `any` on the wire).
 * Read through this shape defensively — none of the fields are guaranteed.
 */
export interface SportsSearchProductMetadata {
    scope?: string;
    competition?: string;
    sports_market_type?: string;
}

interface BaseSearchResultSeries<TCategory extends string = BECategory> {
    category: TCategory;
    series_ticker: string;
    series_title: string;
    event_ticker: string;
    event_title: string;
    event_subtitle: string;
    next_closing_market_ts?: string;
    markets: SearchResultMarket[];
    search_score: number;

    collection_ticker?: string;
    is_closing: boolean;
    is_new: boolean;
    is_price_delta: boolean;
    is_trending: boolean;
    total_market_count: number;
    total_series_volume: number;
    total_volume: number;
    /** Cumulative notional volume (dollars) for the series; present on margin/perps results. */
    total_notional_volume?: number;
    product_metadata?: any;
    tracking_data?: any;
    fee_type?: FeeType;
    fee_multiplier?: number;
    // BFF-style fields
    product_metadata_derived?: { [variable: string]: string };
    active_market_count?: number;
    milestone_id?: string;
    general_title_super?: string;
    tags?: string[];
    // Comboable-search (game-grouped) fields
    prop_events?: SearchPropEvent[];
    /** True when every market that matched the query is a player prop. */
    player_props_only?: boolean;
    // Margin (perps) fields
    last_price?: number | string;
    previous_price?: number | string;
    recent_volume?: number | string;
    funding_rate?: number | string;
}

type MarginSearchNumeric = number | string;

export interface MarginSearchProductMetadata {
    asset_name?: string;
    category?: string;
    chart_gradient_start_color?: string;
    chart_gradient_start_dark_color?: string;
    chart_gradient_stop_color?: string;
    chart_gradient_stop_dark_color?: string;
    chart_line_color?: string;
    chart_line_dark_color?: string;
    competition?: string | null;
    custom_image_url?: string;
    decimal_count?: number;
    description?: string;
    funding_excludes_holidays?: boolean;
    funding_schedule?: string;
    index_provider?: string;
    live_data_asset?: string;
    keywords?: string[];
    market_screen_title?: string;
    shareable_accent_color?: string;
    shareable_layer2_background_color?: string;
    shareable_layer2_border_color?: string;
    shareable_layer2_color?: string;
    shareable_layer2_gradient_bottom_color?: string;
    shareable_layer2_gradient_top_color?: string;
    subaccount_id?: number;
    subcategory?: string;
    trading_hours?: string;
    [key: string]: unknown;
}

export interface ContractSearchResult extends BaseSearchResultSeries<BECategory> {
    type?: MultivariateEventType | 'contract';
}

export interface MarginSearchResult extends BaseSearchResultSeries<string> {
    type: 'margin';
    last_price?: MarginSearchNumeric;
    previous_price?: MarginSearchNumeric;
    recent_volume?: MarginSearchNumeric;
    recent_notional_volume?: MarginSearchNumeric;
    funding_rate?: MarginSearchNumeric;
    contract_size?: MarginSearchNumeric;
    leverage_estimate?: number;
    product_metadata?: MarginSearchProductMetadata;
}

export type SearchResultSeries = ContractSearchResult | MarginSearchResult;

interface RawSuggestedSearchCategoryPageBase {
    category?: string | null;
    subcategory?: string | null;
    competition?: string | null;
    count?: number | null;
}

export interface RawSuggestedSearchCategoryPage extends RawSuggestedSearchCategoryPageBase {
    type: 'category';
}

export interface RawSuggestedSearchSubcategoryPage extends RawSuggestedSearchCategoryPageBase {
    type: 'subcategory';
}

export interface RawSuggestedSearchCompetitionPage extends RawSuggestedSearchCategoryPageBase {
    type: 'competition';
}

export interface RawSuggestedSearchMilestonePage extends RawSuggestedSearchCategoryPageBase {
    type: 'milestone';
    milestone_id?: string | null;
    milestone_title?: string | null;
}

export interface RawSuggestedSearchTopicPage extends RawSuggestedSearchCategoryPageBase {
    type: 'topic';
    topic_keyword?: string | null;
}

/**
 * Forward-compatible shape for page types this version of the client does not
 * understand. Runtime normalization drops these entries.
 */
export interface RawSuggestedSearchUnknownPage extends RawSuggestedSearchCategoryPageBase {
    type?: string | null;
    milestone_id?: string | null;
    milestone_title?: string | null;
}

export type RawSuggestedSearchPage =
    | RawSuggestedSearchCategoryPage
    | RawSuggestedSearchSubcategoryPage
    | RawSuggestedSearchCompetitionPage
    | RawSuggestedSearchMilestonePage
    | RawSuggestedSearchTopicPage
    | RawSuggestedSearchUnknownPage;

export interface RawSuggestedSearchSubcategory {
    category?: string | null;
    subcategory?: string | null;
    competition?: string | null;
    count?: number | null;
}

export interface SearchSeriesResultVo {
    total_results_count: number;
    current_page: SearchResultSeries[];
    next_cursor: string;
    suggested_pages?: RawSuggestedSearchPage[] | null;
    suggested_subcategories?: RawSuggestedSearchSubcategory[] | null;
    /**
     * With `match_structured_targets`, the structured-target ids the query
     * matched, in relevance order. Hydrate `structured_targets` to render them.
     */
    matched_structured_target_ids?: string[] | null;
    hydrated_data?: {
        milestones?: Record<string, MilestoneVo>;
        structured_targets?: Record<string, StructuredTargetVo>;
        events?: Record<string, EventVo>;
    };
}
export interface SearchRelatedSeriesResultVo {
    related_series: SearchResultSeries[];
}

export interface SearchProfilesParams {
    query?: string;
    cursor?: string;
    limit?: number;
}

export interface SearchResultProfiles {
    is_notable?: boolean;
    badge_image_url?: string | null;
    nickname: string;
    profile_image_path: string;
}

export interface SearchProfilesResultVo {
    profiles: SearchResultProfiles[];
    cursor: string;
}

export const sortsCriteriaToNames: Record<SearchSeriesSortCriteria, string> = {
    [SearchSeriesSortCriteria.VOLUME]: 'Volume',
    [SearchSeriesSortCriteria.TRENDING]: 'Trending',
    [SearchSeriesSortCriteria.PRICE_DELTA]: 'Volatile',
    [SearchSeriesSortCriteria.PRICE_BALANCE]: '50-50',
    [SearchSeriesSortCriteria.CLOSING]: 'Closing soon',
    [SearchSeriesSortCriteria.NEWEST]: 'New',
    [SearchSeriesSortCriteria.NEWEST_EVENT]: 'Newest event',
    [SearchSeriesSortCriteria.QUERY_MATCH]: 'Sort by',
    [SearchSeriesSortCriteria.ROUNDROBIN]: 'Round robin',
    [SearchSeriesSortCriteria.START_TIME]: 'Start time',
};

export interface FilterBySportsCompetition {
    scopes: string[];
    scopeCounts?: Record<string, number>;
    recent_volume?: number;
    ranking_score?: number;
    count?: number;
}

export interface FilterBySportsTab {
    label: string;
    display_label?: string;
    event_tickers: string[];
    subtitle?: string;
    layout_type?: string;
    override_layout_type?: string;
    /**
     * With `group_props_by_scope=true`, a stat tab carries one sub-tab per market format —
     * `Ladder` / `Escalator` / `Event` (the plain props) — each with its own event tickers.
     * `event_tickers` on the parent stay the plain props. See `scalarPropFilters.ts`.
     */
    tabs?: FilterBySportsTab[];
}

export interface FilterBySportsItem {
    scopes: string[];
    scopeCounts?: Record<string, number>;
    competitions: Record<string, FilterBySportsCompetition>;
    layout_type: string;
    tabs: FilterBySportsTab[];
    count?: number;
    high_level_filter?: string;
    /**
     * What the chip shows. The filters_by_sport key is an identifier: a scope whose tabs split
     * across high-level filters (series_grouping_override) gets a key like "Spread-Period Props".
     */
    display_label?: string;
}
export interface FilterBySportsParams {
    milestone_id?: string;
    with_counts?: boolean;
    with_recent_volume?: boolean;
    include_high_level_filter?: boolean;
    include_props?: boolean;
    /** Nest ladder/escalator format tabs under the stat tab they count (milestone requests). */
    group_props_by_scope?: boolean;
}

export interface SeriesFeeChange {
    series_ticker: string;
    fee_type: FeeType;
    fee_multiplier: number;
    scheduled_ts: string;
}

export interface SeriesFeeChangesResponse {
    series_fee_change_arr: SeriesFeeChange[];
}
